/**
 * The one control for correcting a day, wherever the owner happens to be looking at one.
 *
 * It used to live inside WorkerDetailPage, which meant a day could only be corrected from the
 * one screen that had it — and that screen is the *least* likely place to be standing when the
 * need arises. You notice a wrong day on Attendance, or in a week grid, or while reading a
 * report; you then had to work out which worker it was, open them, find the month, click the
 * day, and scroll. Most people would conclude the feature did not exist. So it lives here now,
 * and the four screens that show days all mount the same component.
 *
 * Two shapes, one form:
 *
 * - `one` — a single worker on a single day. It loads whatever correction is already there, so
 *   the form opens showing the current state rather than an empty box that silently replaces it,
 *   and it can remove the correction as well as change it.
 * - `many` — a list of (worker, day) pairs with one note across all of them. Whole days only:
 *   hours are per-person by nature, and a batch that asked for them would be the single-day form
 *   again with extra steps.
 *
 * The note is mandatory in both, and the server enforces that independently — this is a
 * convenience, not the rule.
 */
import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { PencilLine } from 'lucide-react';
import { api, del, post, put } from '../lib/api.js';
import { errorMessage } from '../lib/passkey.js';
import { Alert, fmtDate } from '../lib/ui.js';
import { SlideOver } from '../lib/SlideOver.js';
import { t } from '../i18n/index.js';

/** The statuses an admin may set. The engine owns `present_no_punchout`; nobody types it in. */
export type AdjustableStatus = 'present' | 'absent' | 'weekly_off' | 'holiday';

/** One selectable row in a batch. `label` is the worker or the date, whichever varies. */
export type AdjustManyItem = {
  user_id: string;
  date: string;
  label: string;
  sub?: string;
  /** Pre-ticked. Everything is ticked by default; a row can opt out of that. */
  preselected?: boolean;
};

export type AdjustRequest =
  | { kind: 'one'; workerId: string; workerName?: string; date: string }
  | { kind: 'many'; title: string; subtitle: string; items: AdjustManyItem[] };

type LoadedDay = {
  adjustment: {
    status: string | null;
    minutes: number | null;
    note: string;
    original_status: string;
  } | null;
};

/**
 * The affordance. A real button with a word on it, in every one of the four places — not a cell
 * that happens to be clickable, which is how this was effectively invisible before.
 */
export function AdjustButton({
  onClick,
  label,
  className = 'secondary small',
  title,
}: {
  /** Given the event so a button inside a clickable cell can stop it bubbling. */
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  label?: string;
  className?: string;
  title?: string;
}) {
  return (
    <button
      type="button"
      className={`adjust-btn ${className}`}
      onClick={onClick}
      title={title ?? t('adj_open')}
    >
      <PencilLine size={14} aria-hidden="true" />
      <span>{label ?? t('adj_open')}</span>
    </button>
  );
}

export function AdjustDay({
  request,
  onClose,
  onSaved,
}: {
  request: AdjustRequest | null;
  onClose: () => void;
  /** Called after a successful write so the page can reload whatever it is showing. */
  onSaved: () => void;
}) {
  const open = request !== null;
  const [status, setStatus] = useState<'' | AdjustableStatus>('');
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [existing, setExisting] = useState<LoadedDay['adjustment']>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const items = request?.kind === 'many' ? request.items : [];
  const keyOf = (i: AdjustManyItem) => `${i.user_id}|${i.date}`;

  // Reset every time it opens, and for `one` go and fetch what is already there. Opening a form
  // pre-filled with the last day you looked at would be a quiet way to overwrite the wrong day.
  useEffect(() => {
    if (!request) return;
    setError(null);
    setStatus('');
    setHours('');
    setNote('');
    setExisting(null);
    if (request.kind === 'many') {
      setPicked(new Set(request.items.filter((i) => i.preselected !== false).map(keyOf)));
      return;
    }
    let cancelled = false;
    api<LoadedDay>(`/admin/workers/${request.workerId}/day?date=${request.date}`)
      .then((d) => {
        if (cancelled || !d.adjustment) return;
        setExisting(d.adjustment);
        setStatus((d.adjustment.status as AdjustableStatus | null) ?? '');
        setHours(
          d.adjustment.minutes != null
            ? (d.adjustment.minutes / 60).toFixed(2).replace(/\.?0+$/, '')
            : '',
        );
        setNote(d.adjustment.note ?? '');
      })
      .catch(() => {
        // A day with no correction yet answers fine; a genuine failure shows up on save, where
        // it can be acted on. Blocking the form here would be worse than opening it empty.
      });
    return () => {
      cancelled = true;
    };
  }, [request]);

  const chosen = useMemo(() => items.filter((i) => picked.has(keyOf(i))), [items, picked]);
  const enoughNote = note.trim().length >= 3;
  const canSave =
    enoughNote && (request?.kind === 'many' ? chosen.length > 0 && status !== '' : true);

  const save = async () => {
    if (!request) return;
    setBusy(true);
    setError(null);
    try {
      if (request.kind === 'many') {
        await post('/admin/adjustments/bulk', {
          targets: chosen.map((i) => ({ user_id: i.user_id, date: i.date })),
          status: status || null,
          note: note.trim(),
        });
      } else {
        const minutes = hours.trim() === '' ? null : Math.round(Number(hours) * 60);
        if (minutes !== null && (!Number.isFinite(minutes) || minutes < 0 || minutes > 24 * 60))
          throw new Error(t('adj_bad_hours'));
        await put(`/admin/workers/${request.workerId}/day/adjustment`, {
          date: request.date,
          status: status || null,
          minutes,
          note: note.trim(),
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (request?.kind !== 'one') return;
    setBusy(true);
    setError(null);
    try {
      await del(`/admin/workers/${request.workerId}/day/adjustment?date=${request.date}`);
      onSaved();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const title = request?.kind === 'many' ? request.title : t('adj_title');
  const subtitle =
    request?.kind === 'many'
      ? request.subtitle
      : [request?.workerName, request ? fmtDate(request.date, false) : '']
          .filter(Boolean)
          .join(' · ');

  return (
    <SlideOver open={open} title={title} subtitle={subtitle} onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {error && <Alert>{error}</Alert>}

        <label>
          {t('adj_status')}
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as '' | AdjustableStatus)}
            required={request?.kind === 'many'}
          >
            <option value="">
              {request?.kind === 'many' ? t('adj_status_choose') : t('adj_status_keep')}
            </option>
            <option value="present">{t('att_present')}</option>
            <option value="absent">{t('att_absent')}</option>
            <option value="weekly_off">{t('att_off')}</option>
            <option value="holiday">{t('hours_st_holiday')}</option>
          </select>
        </label>

        {/* Hours are one person's, so they belong to the single-day form only. */}
        {request?.kind === 'one' && (
          <label>
            {t('adj_hours')}
            <input
              type="number"
              min={0}
              max={24}
              step={0.25}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder={t('adj_hours_keep')}
            />
          </label>
        )}

        <label>
          {t('adj_note')}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            required
            minLength={3}
            maxLength={500}
            rows={3}
            placeholder={t('adj_note_hint')}
          />
        </label>
        {request?.kind === 'many' && <p className="muted">{t('adj_many_note_hint')}</p>}

        <div className="row">
          <button type="submit" disabled={busy || !canSave}>
            {request?.kind === 'many' ? t('adj_save_many', { n: chosen.length }) : t('save')}
          </button>
          {request?.kind === 'one' && existing && (
            <button type="button" className="danger" disabled={busy} onClick={() => void remove()}>
              {t('adj_remove')}
            </button>
          )}
          <button type="button" className="ghost" onClick={onClose} disabled={busy}>
            {t('cancel')}
          </button>
        </div>
      </form>
    </SlideOver>
  );
}


/**
 * The action bar for a selection made somewhere else.
 *
 * The list of tick-boxes this replaces asked the reader to pick days out of a list while a
 * calendar showing those same days, colour-coded, sat behind the panel — two pictures of one
 * thing, and the wrong one on top. Selection now happens on the calendar (or on the worker list),
 * and this is only the part that could not: what to set them to, and why.
 *
 * It sits at the bottom of the window rather than in a slide-over for the same reason: a form
 * that covers what you are choosing is a form you cannot check before pressing Apply.
 */
export function AdjustSelectionBar({
  count,
  subtitle,
  onApply,
  onCancel,
}: {
  count: number;
  /** Who or what is being changed — "Hemanth Android · September 2026". */
  subtitle: string;
  onApply: (status: AdjustableStatus, note: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [status, setStatus] = useState<'' | AdjustableStatus>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = count > 0 && status !== '' && note.trim().length >= 3;

  const apply = async () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await onApply(status as AdjustableStatus, note.trim());
      setStatus('');
      setNote('');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="adjust-bar" role="region" aria-label={t('adj_bar_label')}>
      <div className="adjust-bar-inner">
        <span className="adjust-bar-count">
          <strong>{t('adj_bar_count', { n: count })}</strong>
          <small className="muted">{subtitle}</small>
        </span>

        <label className="adjust-bar-field">
          <span>{t('adj_status')}</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as '' | AdjustableStatus)}
          >
            <option value="">{t('adj_status_choose')}</option>
            <option value="present">{t('att_present')}</option>
            <option value="absent">{t('att_absent')}</option>
            <option value="weekly_off">{t('att_off')}</option>
            <option value="holiday">{t('hours_st_holiday')}</option>
          </select>
        </label>

        <label className="adjust-bar-field grow">
          <span>{t('adj_note')}</span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('adj_note_hint')}
            maxLength={500}
          />
        </label>

        <span className="row">
          <button type="button" onClick={() => void apply()} disabled={busy || !ready}>
            {busy ? t('adj_bar_applying') : t('adj_bar_apply', { n: count })}
          </button>
          <button type="button" className="ghost" onClick={onCancel} disabled={busy}>
            {t('cancel')}
          </button>
        </span>
      </div>
      {error && <Alert>{error}</Alert>}
      {!error && <p className="muted adjust-bar-hint">{t('adj_bar_hint')}</p>}
    </div>
  );
}
